import { roundCents } from '@/utils/money'
import type { CalculatorContext } from '@/types/calculator'
import type { BondInputs, BondResult } from './types'

export function calculateBond(inputs: BondInputs, _context: CalculatorContext): BondResult {
  const loanAmount = Math.max(0, inputs.propertyPrice - inputs.deposit)
  const monthlyRate = inputs.interestRate / 12
  const numberOfPayments = inputs.termYears * 12

  let monthlyRepayment = 0
  let totalRepayment = 0
  let totalInterest = 0

  if (loanAmount > 0 && monthlyRate > 0) {
    const factor = Math.pow(1 + monthlyRate, numberOfPayments)
    monthlyRepayment = (loanAmount * monthlyRate * factor) / (factor - 1)
    totalRepayment = monthlyRepayment * numberOfPayments
    totalInterest = totalRepayment - loanAmount
  } else if (loanAmount > 0) {
    monthlyRepayment = loanAmount / numberOfPayments
    totalRepayment = loanAmount
    totalInterest = 0
  }

  const depositPercent = inputs.propertyPrice > 0 ? inputs.deposit / inputs.propertyPrice : 0
  const maxRecommendedRepayment = inputs.monthlyIncome * inputs.maxDebtToIncomeRatio
  const monthlyDebtService = inputs.monthlyDebt + monthlyRepayment
  const monthlyAffordabilityRatio =
    inputs.monthlyIncome > 0 ? monthlyDebtService / inputs.monthlyIncome : 0

  let affordable: BondResult['affordable'] = 'yes'
  if (monthlyRepayment > maxRecommendedRepayment || monthlyAffordabilityRatio > 0.45) {
    affordable = 'no'
  } else if (
    monthlyRepayment > maxRecommendedRepayment * 0.85 ||
    monthlyAffordabilityRatio > 0.35
  ) {
    affordable = 'maybe'
  }

  return {
    loanAmount: roundCents(loanAmount),
    depositPercent,
    monthlyRepayment: roundCents(monthlyRepayment),
    totalRepayment: roundCents(totalRepayment),
    totalInterest: roundCents(totalInterest),
    monthlyAffordabilityRatio,
    maxRecommendedRepayment: roundCents(maxRecommendedRepayment),
    affordable,
  }
}
